import { PermissionsAndroid, Platform } from 'react-native';
import { launchCamera, launchImageLibrary } from 'react-native-image-picker';

/**
 * Getting photographs into the app, from either of the two places they live.
 *
 * Three screens attach photographs - a review, a support ticket, and the
 * profile avatar - and every one of them had its own copy of the same picker
 * call with the same four options. They are one thing, so they are one
 * function, and the camera added below is added to all three at once rather
 * than to whichever one was being worked on that day.
 *
 * ---------------------------------------------------------------------------
 * THE CAMERA IS NOT JUST A SECOND BUTTON
 * ---------------------------------------------------------------------------
 * `launchImageLibrary` needs no permission on any current Android: the system
 * picker runs out of process and hands back a URI. `launchCamera` does - it
 * opens the hardware inside our own process - so on Android it must ask, and
 * a tap that opens nothing because the request was never made looks exactly
 * like a crash.
 *
 * So `capture` asks first, and says which of the three things happened:
 * granted, refused once, or refused permanently. The last of those is the one
 * worth telling the caller about, because no amount of tapping will fix it -
 * only Settings will - and the caller is the one holding the sheet that can
 * say so.
 *
 * iOS needs no equivalent: the picker itself raises the system prompt, and
 * `Info.plist` carries `NSCameraUsageDescription` for it.
 *
 * ---------------------------------------------------------------------------
 * DOWNSCALED ON THE WAY IN, ALWAYS
 * ---------------------------------------------------------------------------
 * These are thumbnails on a review card and evidence in a dispute - neither
 * needs twelve megapixels. A photograph straight off a modern phone camera is
 * several times the size of one picked from the library, because the library
 * copy has usually been through a share sheet already, so the ceiling matters
 * more here than it did when this was library-only.
 */

/** Longest edge, and JPEG quality, for anything coming in. */
const LIMITS = {
  maxWidth: 1280,
  maxHeight: 1280,
  quality: 0.8,
} as const;

/** One picked photograph, in the shape `ReviewPhoto` wants. */
export type PickedPhoto = { id: string; uri: string };

export type PickResult = {
  /** What came back. Empty when the picker was cancelled. */
  photos: PickedPhoto[];
  /**
   * Why nothing came back, when it is worth saying out loud.
   *
   * Only set for a permanent refusal. A plain cancel and a one-off "deny" are
   * both deliberate answers to a question the person was just asked, and
   * telling them what they already know is noise.
   */
  blocked?: 'camera-permission';
};

const EMPTY: PickResult = { photos: [] };

/**
 * Turns whatever the picker returned into our own shape.
 *
 * Indexed as well as stamped: a multi-select returns its assets in the same
 * millisecond, and an id collision would have React reuse one tile for two
 * different photographs.
 */
const toPhotos = (
  assets: { uri?: string }[] | undefined,
  room: number,
): PickedPhoto[] =>
  (assets ?? [])
    .map(asset => asset.uri)
    .filter((uri): uri is string => !!uri)
    .slice(0, room)
    .map((uri, i) => ({ id: `${Date.now()}-${i}`, uri }));

/**
 * Asks for the camera, once, and reports which answer came back.
 *
 * `check` first so a granted permission does not re-open a dialog - Android
 * would return GRANTED without showing one, but the round trip is visible as
 * a stutter before the camera opens.
 */
const askForCamera = async (): Promise<'ok' | 'denied' | 'blocked'> => {
  if (Platform.OS !== 'android') {
    return 'ok';
  }

  const already = await PermissionsAndroid.check(
    PermissionsAndroid.PERMISSIONS.CAMERA,
  ).catch(() => false);
  if (already) {
    return 'ok';
  }

  const result = await PermissionsAndroid.request(
    PermissionsAndroid.PERMISSIONS.CAMERA,
  ).catch(() => PermissionsAndroid.RESULTS.DENIED);

  if (result === PermissionsAndroid.RESULTS.GRANTED) {
    return 'ok';
  }
  return result === PermissionsAndroid.RESULTS.NEVER_ASK_AGAIN
    ? 'blocked'
    : 'denied';
};

/**
 * Picks from the library.
 *
 * @param room How many more will fit. Passed to the picker as its selection
 *   limit AND applied again to the result, because the limit is advisory on
 *   some Android pickers and a strip that accepts six of five is worse than
 *   one that quietly drops the sixth.
 */
export const pickFromLibrary = async (room: number): Promise<PickResult> => {
  if (room <= 0) {
    return EMPTY;
  }

  const result = await launchImageLibrary({
    mediaType: 'photo',
    selectionLimit: room,
    ...LIMITS,
  }).catch(() => null);

  return { photos: toPhotos(result?.assets, room) };
};

/**
 * Takes one with the camera.
 *
 * One, not `room`: the camera returns a single shot per launch whatever the
 * selection limit says, and passing one through would only imply otherwise.
 *
 * `saveToPhotos: false` on purpose. Somebody photographing a stain on their
 * sofa to attach to a complaint has not asked for it in their camera roll,
 * and on Android saving there would need a second permission on older
 * versions for no gain.
 */
export const captureWithCamera = async (
  room: number,
): Promise<PickResult> => {
  if (room <= 0) {
    return EMPTY;
  }

  const permission = await askForCamera();
  if (permission !== 'ok') {
    return permission === 'blocked'
      ? { photos: [], blocked: 'camera-permission' }
      : EMPTY;
  }

  const result = await launchCamera({
    mediaType: 'photo',
    saveToPhotos: false,
    ...LIMITS,
  }).catch(() => null);

  return { photos: toPhotos(result?.assets, 1) };
};
